import React, { useState, useEffect } from 'react';
import styles from './styles/CategoryModal.module.css';
import { updateUser } from '../../services/UserServices';

const CategoryModal = ({ onClose, scenarios, accountOwner, setAccountOwner, category }) => {
    const [label, setLabel] = useState(category ? category.label : '');
    const [color, setColor] = useState(category ? category.color : '#000000');
    const [selectedScenarios, setSelectedScenarios] = useState(category ? category.scenarios : []);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredScenarios, setFilteredScenarios] = useState([]);

    useEffect(() => {
        const sortedScenarios = [...scenarios].sort((a, b) =>
            a.title.localeCompare(b.title)
        );
    
        let filtered = sortedScenarios;
    
        if (searchQuery) {
            const query = searchQuery.trim().toLowerCase();
    
            if (query.startsWith('#')) {
                const tag = query.slice(1);
                filtered = filtered.filter(scenario =>
                    scenario.tags.some(tagItem => tagItem.toLowerCase().includes(tag))
                );
            } else {
                filtered = filtered.filter(scenario =>
                    scenario.title.toLowerCase().includes(query)
                );
            }
        }
    
        setFilteredScenarios(filtered);
    }, [searchQuery, scenarios]);
    

    const handleLabelChange = (event) => {
        setLabel(event.target.value);
    };

    const handleColorChange = (event) => {
        setColor(event.target.value);
    };

    const handleScenarioClick = (scenarioId) => {
        setSelectedScenarios(prevSelected =>
            prevSelected.includes(scenarioId)
                ? prevSelected.filter(id => id !== scenarioId)
                : [...prevSelected, scenarioId]
        );
    };

    const handleSaveCategory = () => {
        const newCategory = {
            label,
            color,
            scenarios: selectedScenarios
        };

        const updatedCategories = category
            ? accountOwner.categories.map(cat =>
                cat.label === category.label ? newCategory : cat
            )
            : [...accountOwner.categories, newCategory];

        updateUser(accountOwner._id, {
            categories: updatedCategories
        }).then(data => {
            setAccountOwner(data);
            onClose();
        }).catch(error => {
            console.error('Error saving category:', error);
        });
    };

    const handleDeleteCategory = () => {
        const updatedCategories = accountOwner.categories.filter(cat => cat.label !== category.label);

        updateUser(accountOwner._id, {
            categories: updatedCategories
        }).then(data => {
            setAccountOwner(data);
            onClose();
        }).catch(error => {
            console.error('Error deleting category:', error);
        });
    };

    return (
        <div className={styles.backdrop} onClick={onClose}>
            <div className={styles.modalDialog} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>{category ? 'Edit Category' : 'Create New Category'}</h2>
                    <button
                        type="button"
                        className={`${styles.buttonIcon} ${styles.modalCloseButton}`}
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                        </svg>
                    </button>
                </div>
    
                <div className={styles.modalBody}>
                    <div className={styles.formSection}>
                        <div className={styles.formGroup}>
                            <label htmlFor="categoryLabel" className={styles.label}>Category Label</label>
                            <input
                                id="categoryLabel"
                                type="text"
                                placeholder="Enter category label"
                                value={label}
                                onChange={handleLabelChange}
                                className={styles.inputField}
                                maxLength={50}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="categoryColor" className={styles.label}>Category Color</label>
                            <div className={styles.colorPickerWrapper}>
                                <input
                                    id="categoryColor"
                                    type="color"
                                    value={color}
                                    onChange={handleColorChange}
                                    className={styles.colorInput}
                                    aria-label="Category color picker"
                                />
                                <span className={styles.colorHexValue}>{color.toUpperCase()}</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.formSection}>
                        <div className={styles.formGroup}>
                            <label htmlFor="scenarioSearch" className={styles.label}>Assign Scenarios</label>
                            <input
                                id="scenarioSearch"
                                type="search"
                                placeholder="Search scenarios to assign..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className={styles.inputField}
                            />
                        </div>
                        <div className={styles.scenarioListContainer} tabIndex={0}>
                            {filteredScenarios.length > 0 ? (
                                filteredScenarios.map(scenario => (
                                    <div
                                        key={scenario._id}
                                        className={`
                                            ${styles.scenarioItem}
                                            ${selectedScenarios.includes(scenario._id) ? styles.selected : ''}
                                        `}
                                        onClick={() => handleScenarioClick(scenario._id)}
                                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleScenarioClick(scenario._id)}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <img src={scenario.image || '/placeholder-image.svg'} alt={scenario.title} className={styles.scenarioImage} />
                                        <span className={styles.scenarioTitle}>{scenario.title}</span>
                                        {selectedScenarios.includes(scenario._id) && (
                                            <svg className={styles.checkmarkIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                            </svg>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <p className={styles.noScenariosMessage}>No scenarios found or available.</p>
                            )}
                        </div>
                    </div>
                    {category && (
                        <div className={`${styles.formSection} ${styles.deleteSection}`}>
                            <hr className={styles.divider} />
                            <button
                                type="button"
                                onClick={handleDeleteCategory}
                                className={`${styles.button} ${styles.dangerButtonOutline}`}
                            >
                                Delete Category
                            </button>
                            <p className={styles.deleteHint}>Deleting a category cannot be undone.</p>
                        </div>
                    )}
                </div>
    
                <div className={styles.modalFooter}>
                    <button
                        type="button"
                        onClick={onClose}
                        className={`${styles.button} ${styles.secondaryButton}`}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSaveCategory}
                        className={`${styles.button} ${styles.primaryButton}`}
                    >
                        {category ? 'Save Changes' : 'Create Category'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CategoryModal;
